/**
 * billableExpense — a DESPESA COBRÁVEL (repassada ao cliente) e sua linha no
 * orçamento, numa fonte única e pura.
 *
 * ─── DECISÃO DE NEGÓCIO: a despesa cobrável é MATERIALIZADA ──────────────────
 *
 * Uma despesa cobrável não é "um número somado na hora de mostrar o orçamento":
 * ela vira uma LINHA de `quote_items`, exatamente como os materiais de um kit
 * (CLAUDE.md #65).
 *
 * Por quê: `quotes.total` é uma COLUNA ARMAZENADA e alimenta o Dashboard, os
 * relatórios e o prefill do financeiro. Se a despesa fosse somada em tempo de
 * render, essa coluna ficaria obsoleta no instante em que alguém lançasse uma
 * despesa depois do save — e a tela do orçamento, o PDF, a lista e o Dashboard
 * passariam a mostrar números diferentes entre si, sem erro nenhum. Materializada,
 * o total sai de graça pela fórmula de {@link ../lib/quoteTotals} e o PDF imprime
 * a linha naturalmente, sem template novo.
 *
 * ─── QUEM É O DONO ───────────────────────────────────────────────────────────
 *
 * O **ORÇAMENTO** é o dono. A despesa cobrável nasce a partir dele e gera as
 * duas coisas de uma vez: a linha em `quote_items` e o lançamento em
 * `finance_entries` (`billable = true` + `quote_id`). Criar em `/financeiro` e
 * ver um orçamento mudar sozinho é ação à distância — por isso o formulário do
 * financeiro só oferece a marcação quando foi aberto A PARTIR de um orçamento.
 *
 * Editar o valor e excluir, esses sim, precisam refletir na linha (senão o
 * documento passa a cobrar um valor que o financeiro não conhece mais).
 *
 * ─── O BLOQUEIO EM DOCUMENTO APROVADO ────────────────────────────────────────
 *
 * Mexer no total de um orçamento já `approved`/`completed` é grave: é um valor
 * que o cliente já viu e possivelmente aceitou. Toda operação passa por
 * {@link isQuoteLockedForBillable} e é RECUSADA com aviso explícito — nunca
 * aplicada em silêncio.
 *
 * ─── A LINHA NÃO É MERCADORIA ────────────────────────────────────────────────
 *
 * A linha materializada tem `unit_cost` **null** de propósito e é reconhecível
 * por `finance_entry_id`. Ela não entra no CMV: a mesma despesa já é contada
 * como despesa no DRE, e contá-la também como custo do produto seria dupla
 * contagem. Ver `src/lib/profitReport.ts`.
 */
import { roundMoney } from '@/lib/pricing';
import type { QuoteItemFormLike } from '@/lib/quoteItems';
import {
  buildQuoteTotalsColumns,
  quoteHeaderFromRow,
  type QuoteTotalsItem,
} from '@/lib/quoteTotals';

/**
 * Status de orçamento em que o total NÃO pode mais ser alterado por uma despesa
 * cobrável. É a mesma dupla que o `StockDeductionPrompt` considera "fechada".
 */
export const BILLABLE_LOCKED_QUOTE_STATUSES = ['approved', 'completed'] as const;

export const BILLABLE_LOCKED_MESSAGE =
  'Este orçamento já foi aprovado. Para não alterar um valor que o cliente já viu, '
  + 'despesas cobráveis não podem ser adicionadas, editadas nem removidas aqui.';

export const BILLABLE_OFFLINE_MESSAGE =
  'Despesa cobrável precisa de conexão: ela altera o total do orçamento e o '
  + 'vínculo não sobrevive à fila offline. Lance como despesa comum ou tente novamente online.';

export const BILLABLE_REQUIRES_QUOTE_MESSAGE =
  'Marque a despesa como cobrável a partir do orçamento — é ele que passa a cobrar o valor.';

/** Texto de ajuda mostrado ao lado da marcação, no formulário do lançamento. */
export const BILLABLE_HELP_TEXT =
  'Despesas cobráveis entram como uma linha no orçamento: o cliente paga por elas '
  + 'e o total do documento aumenta. O gasto continua registrado aqui no financeiro.';

/**
 * Evento disparado quando uma despesa cobrável altera o documento.
 *
 * Dedicado de propósito, em vez de reaproveitar `quotes-updated`: a tela do
 * orçamento também dispara `quotes-updated` no PRÓPRIO save, e escutar aquele
 * faria a página recarregar sozinha depois de cada gravação. Aqui o que
 * importa é o caso em que a linha e o total mudaram POR FORA do formulário —
 * sem este aviso, a tela continuaria mostrando o total antigo e o item novo só
 * apareceria no próximo carregamento.
 */
export const BILLABLE_EXPENSES_UPDATED_EVENT = 'billable-expenses-updated';

export function notifyBillableExpensesUpdated(): void {
  window.dispatchEvent(new CustomEvent(BILLABLE_EXPENSES_UPDATED_EVENT));
}

export function isQuoteLockedForBillable(status: string | null | undefined): boolean {
  return (BILLABLE_LOCKED_QUOTE_STATUSES as readonly string[]).includes(String(status ?? ''));
}

/** A linha materializada, reconhecida pelo vínculo (nunca pelo nome). */
export function isBillableQuoteLine(
  item: { finance_entry_id?: string | null } | null | undefined,
): boolean {
  return Boolean(item?.finance_entry_id);
}

/** O que a UI/o save precisam saber de uma despesa cobrável. */
export interface BillableExpenseSource {
  /** Id do lançamento (`finance_entries.id`) — é ele que ancora a linha. */
  id: string;
  description: string;
  amount: number | string | null | undefined;
}

/**
 * A linha de orçamento equivalente a uma despesa cobrável.
 *
 * Quantidade 1 e preço = valor da despesa: é um repasse, não uma venda por
 * unidade. `unit_cost` fica ausente (→ `null` no builder) porque a linha NÃO é
 * mercadoria — ver a nota de dupla contagem no topo. Sem desconto: descontar um
 * repasse faria o prestador pagar a diferença do próprio bolso.
 */
export function buildBillableQuoteItem(expense: BillableExpenseSource): QuoteItemFormLike {
  const amount = Number(expense.amount ?? 0);
  return {
    name: expense.description?.trim() || 'Despesa',
    description: null,
    quantity: 1,
    unit_price: roundMoney(Number.isFinite(amount) ? Math.max(0, amount) : 0),
    discount: 0,
    discount_type: 'value',
    itemType: 'service',
    unit: null,
    image_url: null,
    product_id: null,
    barcode: null,
    // Ausente de propósito: repasse não é mercadoria e não entra no CMV.
    unit_cost: null,
    finance_entry_id: expense.id,
  };
}

/**
 * As quatro colunas de total de `quotes` recalculadas a partir do cabeçalho
 * gravado e da lista de itens que o documento passa a ter.
 *
 * Existe para que os três caminhos (criar, editar valor, excluir a despesa)
 * recalculem do mesmo jeito. Reaproveita `quoteHeaderFromRow`, então frete e
 * desconto de cabeçalho do G3 continuam sendo respeitados — recalcular só a
 * soma dos itens apagaria os dois.
 */
export function recomputeQuoteTotalsColumns(
  quoteRow: { shipping?: number | string | null; discount?: number | string | null; discount_type?: string | null } | null | undefined,
  items: readonly QuoteTotalsItem[],
) {
  return buildQuoteTotalsColumns({ ...quoteHeaderFromRow(quoteRow), items });
}
